import axios from "axios";
import {
  postToInstagram,
  postToLinkedIn,
  postToTwitter,
  postToFacebook,
  postToYoutube,
} from "./socialMediaAPI.js";
import { API_URL, COUNTRY_API_URL } from "../Config/index.js";
import { emitEvent } from "../MasterData/emitter";
import { getAuthTokenPayload } from "../Utils/index.js";

// Helper function to get current clientId from the JWT auth token payload
const getClientId = () => {
  return getAuthTokenPayload()?.clientId ?? null;
};

// Helper function to get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem("authToken");
};

const handleSessionTimeout = (status, data) => {
  if (status === 401 && data?.sessionTimeout) {
    emitEvent("session-timeout", data);
    return true;
  }
  return false;
};

// Fetch pricing plans from API
export const fetchPricingPlans = async (r1) => {
  try {
    const response = await fetch(`${API_URL}/payment/${r1}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    const result = await response.json().catch(() => ({}));
    if (result.status && result.data?.mapValue) {
      return { success: true, data: result.data.mapValue };
    }
    return { success: false, data: [], message: result.uiMsg || result.message || "Failed to load plans" };
  } catch (error) {
    console.error("Error fetching pricing plans:", error);
    return { success: false, data: [], message: error.message || "Network error" };
  }
};

// Add this function to your existing api.js file
export const fetchCountries = async () => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${COUNTRY_API_URL}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `${token}` } : {}),
      },
    });
    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: [],
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (result.status && result.data) {
      return {
        success: true,
        data: result.data,
        count: result.count,
        message: result.message,
      };
    } else {
      return {
        success: false,
        data: [],
        message: result.message || "Failed to fetch countries",
      };
    }
  } catch (error) {
    console.error("Error fetching countries:", error);
    return {
      success: false,
      data: [],
      message: error.message || "Network error occurred",
    };
  }
};

/**
 * Add a new user/employee
 * @param {Object} userData - User data to submit
 * @returns {Promise<Object>} API response
 */
export const addUser = async (userData) => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${API_URL}/manage-office/add/user`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `${token}` } : {}),
      },
      body: JSON.stringify(userData),
    });

    // Get response details for better error handling
    const responseText = await response.text();
    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      responseData = { message: responseText };
    }

    if (handleSessionTimeout(response.status, responseData)) {
      return {
        success: false,
        message: responseData.uiMsg || responseData.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(
        `API Error (${response.status}): ${
          responseData.message || responseText || "Unknown error"
        }`,
      );
    }

    return responseData;
  } catch (error) {
    console.error("Error adding user:", error);
    throw error;
  }
};

// User Login API
export const userLogin = async (requestPayload) => {
  try {
    const response = await axios.post(
      `${API_URL}/user-auth/login/1`,
      requestPayload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response;
  } catch (error) {
    console.log("Login error:", error);
    return error;
  }
};

// export const userGoogleLogin = async (token) => {
//   try {
//     console.log("userGoogleLogin called, token length:", token?.length, "token preview:", token?.substring(0, 20));
//     const response = await axios.post(
//       `${API_URL}/user-auth/OAuth2Client/login`,
//       { token },
//       {
//         headers: {
//           "Content-Type": "application/json",
//         },
//       },
//     );
//     return response;
//   } catch (error) {
//     console.log("Google login error:", error);
//     return error;
//   }
// };

export const userGoogleLogin = async (token, phoneNumber = null) => {
  try {
    const payload = { token };
    
    // Only include phoneNumber in payload if it's provided
    if (phoneNumber) {
      payload.phoneNumber = phoneNumber;
    }
    
    const response = await axios.post(
      `${API_URL}/user-auth/OAuth2Client/login`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response;
  } catch (error) {
    console.log("Google login error:", error);
    return error;
  }
};

// Get User Tasks API
export const getUsersAPI = async () => {
  // Trim the userId to remove any accidental spaces
  try {
    // const response = await axios.get(`${API_URL}/user-auth/userinfo/1`);
    const clientId = getClientId();
    if (!clientId) {
      throw new Error("Client ID not found. Please log in again.");
    }

    const token = getAuthToken();
    const response = await axios.get(
      `${API_URL}/user-auth/userinfo/${clientId}`,
      {
        headers: {
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );
    return response;
  } catch (error) {
    console.log("Error in getTasksAPI:", error);
    throw error;
  }
};

// User Login API
export const createUser = async (requestPayload) => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      throw new Error("Client ID not found. Please log in again.");
    }

    const token = getAuthToken();
    const response = await axios.patch(
      `${API_URL}/user-auth/login/1`,
      {
        ...requestPayload,
        clientId: clientId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );
    return response;
  } catch (error) {
    console.log("Login error:", error);
    return error;
  }
};

// Edit Users
export const editUser = async (requestPayload) => {
  try {
    const token = getAuthToken();
    const response = await axios.patch(
      `${API_URL}/user-auth/update/UserPermission`,
      requestPayload,
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );
    return response;
  } catch (error) {
    console.log("Login error:", error);
    return error;
  }
};

// Change Password API
export const changePassword = async (userData) => {
  try {
    console.log("Making password change request:", userData);

    const token = getAuthToken();
    const response = await fetch(`${API_URL}/user-auth/change-password/2`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `${token}` } : {}),
      },
      body: JSON.stringify(userData),
    });

    console.log("Raw response:", response);
    const data = await response.json();
    console.log("Parsed response:", data);

    return data;
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

// User Action API (remove / deactivate / activate)
// DELETE ${API_URL}/user-auth/{userId}/{action}
export const userAction = async (userId, action) => {
  try {
    const token = getAuthToken();
    const response = await axios.delete(
      `${API_URL}/user-auth/${userId}/${action}`,
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );
    return response;
  } catch (error) {
    console.log(`User action (${action}) error:`, error);
    return error;
  }
};

/**
 * Fetch leave types for a specific country
 * @param {string} countryCode - The country code (e.g., 'IN' for India)
 * @returns {Promise<Array>} - Array of leave types
 */

// Add this new function to your existing api.js file
export const fetchAccountNames = async () => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: [],
        message: "Client ID not found. Please log in again.",
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/GetAccountName/${clientId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: [],
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status && result.data) {
      // Transform API response to match the expected format
      const transformedAccounts = result.data.map((account) => ({
        accountId: account.accountId,
        accountName: account.accountName,
        // Add any other properties you need
        id: account.accountId, // For compatibility with existing code
      }));

      return {
        success: true,
        data: transformedAccounts,
        message: result.message || "Successfully loaded accounts",
      };
    } else {
      return {
        success: false,
        data: [],
        message: result.message || "Failed to fetch accounts",
      };
    }
  } catch (error) {
    console.error("Error in fetchAccountNames:", error);
    return {
      success: false,
      data: [],
      message:
        error.message || "Network error occurred while fetching accounts",
    };
  }
};

// Initiate Twitter OAuth flow - returns the authUrl to redirect the user to
export const initiateTwitterOAuth = async () => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        authUrl: null,
        message: "Client ID not found. Please log in again.",
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/twitter-oauth/${clientId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        authUrl: null,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status && result.data?.authUrl) {
      return {
        success: true,
        authUrl: result.data.authUrl,
        message: result.message || "Twitter OAuth initiation successful",
      };
    }

    return {
      success: false,
      authUrl: null,
      message: result.message || "Failed to initiate Twitter connection",
    };
  } catch (error) {
    console.error("Error in initiateTwitterOAuth:", error);
    return {
      success: false,
      authUrl: null,
      message: error.message || "Network error occurred while connecting to Twitter",
    };
  }
};

export const createNewAccount = async (accountData) => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: null,
        message: "Client ID not found. Please log in again.",
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/createNewAccount/${clientId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
        body: JSON.stringify(accountData),
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: null,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status) {
      return {
        success: true,
        data: result.data,
        message: result.message || "Account created successfully",
      };
    } else {
      return {
        success: false,
        data: null,
        message: result.message || "Failed to create account",
      };
    }
  } catch (error) {
    console.error("Error in createNewAccount:", error);
    return {
      success: false,
      data: null,
      message: error.message || "Network error occurred while creating account",
    };
  }
};

// Add this new function to your existing api.js file
export const createAccountGroup = async (groupData) => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: null,
        message: "Client ID not found. Please log in again.",
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/createNewAccountGroup/${clientId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
        body: JSON.stringify({
          entityType: "Account",
          groupName: groupData.groupName,
          description: groupData.description,
          groupType: groupData.groupType,
          country: groupData.country,
          status: groupData.status,
          accountId: groupData.accountIds || [],
        }),
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: null,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status) {
      return {
        success: true,
        data: result.data,
        message: result.message || "Group created successfully",
      };
    } else {
      return {
        success: false,
        data: null,
        message: result.message || "Failed to create group",
      };
    }
  } catch (error) {
    console.error("Error in createAccountGroup:", error);
    return {
      success: false,
      data: null,
      message: error.message || "Network error occurred while creating group",
    };
  }
};

// NEW: This function is for creating ACCOUNTS
export const createAccount = async (accountData) => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: null,
        message: "Client ID not found. Please log in again.",
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/createAccount/${clientId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
        body: JSON.stringify(accountData),
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: null,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status) {
      return {
        success: true,
        data: result.data,
        message: result.message || "Account created successfully",
      };
    } else {
      return {
        success: false,
        data: null,
        message: result.message || "Failed to create account",
      };
    }
  } catch (error) {
    console.error("Error in createAccount:", error);
    return {
      success: false,
      data: null,
      message: error.message || "Network error occurred while creating account",
    };
  }
};

// Add this new function to your existing api.js file
export const fetchGroupNames = async () => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: [],
        message: "Client ID not found. Please log in again.",
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/GetGroupName/${clientId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: [],
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status && result.data) {
      // Transform API response to match the expected format
      const transformedGroups = result.data.map((group) => ({
        groupId: group.groupId,
        groupName: group.groupName,
        // Add any other properties you need
        id: group.groupId, // For compatibility with existing code
      }));

      return {
        success: true,
        data: transformedGroups,
        message: result.message || "Successfully loaded groups",
      };
    } else {
      return {
        success: false,
        data: [],
        message: result.message || "Failed to fetch groups",
      };
    }
  } catch (error) {
    console.error("Error in fetchGroupNames:", error);
    return {
      success: false,
      data: [],
      message: error.message || "Network error occurred while fetching groups",
    };
  }
};

// Add this new function to your existing api.js file
export const loadAllGroupAndAccountData = async () => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: {
          detailsData: [],
          totalDetailsRetrieved: 0,
          summary: {},
        },
        message: "Client ID not found. Please log in again.",
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/GroupAndAccountData/${clientId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: {
          detailsData: [],
          totalDetailsRetrieved: 0,
          summary: {},
        },
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status && result.data) {
      return {
        success: true,
        data: {
          detailsData: result.data.detailsData || [],
          totalDetailsRetrieved: result.data.totalDetailsRetrieved || 0,
          summary: result.data.summary || {},
        },
        message: result.message || "Successfully loaded account group details",
      };
    } else {
      return {
        success: false,
        data: {
          detailsData: [],
          totalDetailsRetrieved: 0,
          summary: {},
        },
        message: result.message || "Failed to load account group details",
      };
    }
  } catch (error) {
    console.error("Error in loadAllGroupAndAccountData:", error);
    return {
      success: false,
      data: {
        detailsData: [],
        totalDetailsRetrieved: 0,
        summary: {},
      },
      message:
        error.message ||
        "Network error occurred while loading account group details",
    };
  }
};

// Add this new function to your existing api.js file
export const editGroupOrAccount = async (editData) => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: null,
        message: "Client ID not found. Please log in again.",
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/editGroupOrAccount/${clientId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
        body: JSON.stringify(editData),
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: null,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status) {
      return {
        success: true,
        data: result.data,
        message: result.message || "Successfully updated group or account",
      };
    } else {
      return {
        success: false,
        data: null,
        message: result.message || "Failed to update group or account",
      };
    }
  } catch (error) {
    console.error("Error in editGroupOrAccount:", error);
    return {
      success: false,
      data: null,
      message:
        error.message ||
        "Network error occurred while updating group or account",
    };
  }
};

// Add this new function to your existing api.js file
export const createNewGroup = async (groupData) => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: null,
        message: "Client ID not found. Please log in again.",
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/createNewGroup/${clientId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
        body: JSON.stringify(groupData),
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: null,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status) {
      return {
        success: true,
        data: result.data,
        message: result.message || "Group created successfully",
      };
    } else {
      return {
        success: false,
        data: null,
        message: result.message || "Failed to create group",
      };
    }
  } catch (error) {
    console.error("Error in createNewGroup:", error);
    return {
      success: false,
      data: null,
      message: error.message || "Network error occurred while creating group",
    };
  }
};

// UPDATED: Enhanced loadAccounts with better error handling
export const loadAccounts = async () => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: { accounts: [], totalAccounts: 0 },
        message: "Client ID not found. Please log in again.",
        isEmpty: true,
        isError: true,
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/loadAccount/${clientId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: { accounts: [], totalAccounts: 0 },
        message: result.uiMsg || result.message || "Session timeout",
        isEmpty: true,
        isError: true,
      };
    }

    // Handle different HTTP status codes
    if (response.status === 404) {
      return {
        success: true, // Treat as success since it's expected when no data exists
        data: { accounts: [], totalAccounts: 0 },
        message:
          'No accounts have been set up yet. Click "Add Account" to get started.',
        isEmpty: true,
      };
    }

    if (!response.ok) {
      throw new Error(
        `Server error: ${response.status} ${response.statusText}`,
      );
    }

    // Handle API response structure
    if (
      result.status === false &&
      result.message === "No account index data found"
    ) {
      return {
        success: true, // Treat as success since it's expected when no data exists
        data: { accounts: [], totalAccounts: 0 },
        message:
          'No accounts have been set up yet. Click "Add Account" to get started.',
        isEmpty: true,
      };
    }

    if (result.status && result.data) {
      return {
        success: true,
        data: result.data,
        message: `${
          result.data.totalAccounts || 0
        } accounts loaded successfully`,
        isEmpty: (result.data.accounts || []).length === 0,
      };
    } else {
      return {
        success: false,
        data: { accounts: [], totalAccounts: 0 },
        message: result.message || "Failed to load accounts data",
        isEmpty: true,
      };
    }
  } catch (error) {
    console.error("Error in loadAccounts:", error);

    // Provide user-friendly error messages
    let userMessage = "Unable to load accounts at this time.";

    if (
      error.message.includes("Failed to fetch") ||
      error.message.includes("Network")
    ) {
      userMessage =
        "Network connection issue. Please check your internet connection and try again.";
    } else if (error.message.includes("Server error: 500")) {
      userMessage =
        "Server is experiencing issues. Please try again in a few moments.";
    } else if (error.message.includes("Server error: 403")) {
      userMessage = "You don't have permission to access accounts data.";
    }

    return {
      success: false,
      data: { accounts: [], totalAccounts: 0 },
      message: userMessage,
      isEmpty: true,
      isError: true,
    };
  }
};

// UPDATED: Enhanced loadGroups with better error handling
export const loadGroups = async () => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: { groups: [], totalGroups: 0 },
        message: "Client ID not found. Please log in again.",
        isEmpty: true,
        isError: true,
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/loadGroup/${clientId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: { groups: [], totalGroups: 0 },
        message: result.uiMsg || result.message || "Session timeout",
        isEmpty: true,
        isError: true,
      };
    }

    // Handle different HTTP status codes
    if (response.status === 404) {
      return {
        success: true, // Treat as success since it's expected when no data exists
        data: { groups: [], totalGroups: 0 },
        message:
          'No account groups have been created yet. Click "Add Group" to get started.',
        isEmpty: true,
      };
    }

    if (!response.ok) {
      throw new Error(
        `Server error: ${response.status} ${response.statusText}`,
      );
    }

    // Handle API response structure
    if (
      result.status === false &&
      result.message &&
      result.message.includes("No") &&
      result.message.includes("data found")
    ) {
      return {
        success: true, // Treat as success since it's expected when no data exists
        data: { groups: [], totalGroups: 0 },
        message:
          'No account groups have been created yet. Click "Add Group" to get started.',
        isEmpty: true,
      };
    }

    if (result.status && result.data) {
      return {
        success: true,
        data: result.data,
        message: `${result.data.totalGroups || 0} groups loaded successfully`,
        isEmpty: (result.data.groups || []).length === 0,
      };
    } else {
      return {
        success: false,
        data: { groups: [], totalGroups: 0 },
        message: result.message || "Failed to load groups data",
        isEmpty: true,
      };
    }
  } catch (error) {
    console.error("Error in loadGroups:", error);

    // Provide user-friendly error messages
    let userMessage = "Unable to load account groups at this time.";

    if (
      error.message.includes("Failed to fetch") ||
      error.message.includes("Network")
    ) {
      userMessage =
        "Network connection issue. Please check your internet connection and try again.";
    } else if (error.message.includes("Server error: 500")) {
      userMessage =
        "Server is experiencing issues. Please try again in a few moments.";
    } else if (error.message.includes("Server error: 403")) {
      userMessage = "You don't have permission to access groups data.";
    }

    return {
      success: false,
      data: { groups: [], totalGroups: 0 },
      message: userMessage,
      isEmpty: true,
      isError: true,
    };
  }
};

// Add this new function to your existing api.js file
export const loadGroupDropdown = async () => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: [],
        totalGroups: 0,
        message: "Client ID not found. Please log in again.",
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/loadGroupDropdown/${clientId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: [],
        totalGroups: 0,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status && result.data) {
      return {
        success: true,
        data: result.data.groups || [],
        totalGroups: result.data.totalGroups || 0,
        message: result.message || "Group dropdown data loaded successfully",
      };
    } else {
      return {
        success: false,
        data: [],
        totalGroups: 0,
        message: result.message || "Failed to load group dropdown data",
      };
    }
  } catch (error) {
    console.error("Error in loadGroupDropdown:", error);
    return {
      success: false,
      data: [],
      totalGroups: 0,
      message:
        error.message ||
        "Network error occurred while loading group dropdown data",
    };
  }
};

// Add this new function to your existing api.js file
export const loadAccountDropdown = async () => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: [],
        totalAccounts: 0,
        message: "Client ID not found. Please log in again.",
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/loadAccountDropdown/${clientId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: [],
        totalAccounts: 0,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status && result.data) {
      return {
        success: true,
        data: result.data.accounts || [],
        totalAccounts: result.data.totalAccounts || 0,
        message: result.message || "Account dropdown data loaded successfully",
      };
    } else {
      return {
        success: false,
        data: [],
        totalAccounts: 0,
        message: result.message || "Failed to load account dropdown data",
      };
    }
  } catch (error) {
    console.error("Error in loadAccountDropdown:", error);
    return {
      success: false,
      data: [],
      totalAccounts: 0,
      message:
        error.message ||
        "Network error occurred while loading account dropdown data",
    };
  }
};

// Remove Account API
export const removeAccount = async (accountId) => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        message: "Client ID not found. Please log in again.",
        data: {},
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/removeAccount/${clientId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
        body: JSON.stringify({
          accountId: accountId,
        }),
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        message: result.uiMsg || result.message || "Session timeout",
        data: {},
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status) {
      return {
        success: true,
        message: result.message || `Account ${accountId} removed successfully`,
        data: result.data || {},
      };
    } else {
      return {
        success: false,
        message: result.message || "Failed to remove account",
        data: {},
      };
    }
  } catch (error) {
    console.error("Error in removeAccount:", error);
    return {
      success: false,
      message: error.message || "Network error occurred while removing account",
      data: {},
    };
  }
};

// Remove Group API
export const removeGroup = async (groupId) => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        message: "Client ID not found. Please log in again.",
        data: {},
      };
    }

    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/removeAccount/${clientId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
        body: JSON.stringify({
          groupId: groupId,
        }),
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        message: result.uiMsg || result.message || "Session timeout",
        data: {},
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status) {
      return {
        success: true,
        message: result.message || `Group ${groupId} removed successfully`,
        data: result.data || {},
      };
    } else {
      return {
        success: false,
        message: result.message || "Failed to remove group",
        data: {},
      };
    }
  } catch (error) {
    console.error("Error in removeGroup:", error);
    return {
      success: false,
      message: error.message || "Network error occurred while removing group",
      data: {},
    };
  }
};

// Load Social Media Accounts API
export const loadSocialMediaAccounts = async (clientId) => {
  try {
    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/account-settings/loadSocialMediaAccount/${clientId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: { accounts: [], totalAccounts: 0 },
        message: result.uiMsg || result.message || "Session timeout",
        isEmpty: true,
        isError: true,
      };
    }

    // Handle different HTTP status codes
    if (response.status === 404) {
      return {
        success: true, // Treat as success since it's expected when no data exists
        data: { accounts: [], totalAccounts: 0 },
        message: "No social media accounts have been connected yet.",
        isEmpty: true,
      };
    }

    if (!response.ok) {
      throw new Error(
        `Server error: ${response.status} ${response.statusText}`,
      );
    }

    // Handle API response structure
    if (
      result.status === false &&
      result.message &&
      result.message.includes("No") &&
      result.message.includes("data found")
    ) {
      return {
        success: true, // Treat as success since it's expected when no data exists
        data: { accounts: [], totalAccounts: 0 },
        message: "No social media accounts have been connected yet.",
        isEmpty: true,
      };
    }

    if (result.status && result.data) {
      return {
        success: true,
        data: result.data,
        message: `${
          result.data.totalAccounts || 0
        } social media accounts loaded successfully`,
        isEmpty: (result.data.accounts || []).length === 0,
      };
    } else {
      return {
        success: false,
        data: { accounts: [], totalAccounts: 0 },
        message: result.message || "Failed to load social media accounts data",
        isEmpty: true,
      };
    }
  } catch (error) {
    console.error("Error in loadSocialMediaAccounts:", error);

    // Provide user-friendly error messages
    let userMessage = "Unable to load social media accounts at this time.";

    if (
      error.message.includes("Failed to fetch") ||
      error.message.includes("Network")
    ) {
      userMessage =
        "Network connection issue. Please check your internet connection and try again.";
    } else if (error.message.includes("Server error: 500")) {
      userMessage =
        "Server is experiencing issues. Please try again in a few moments.";
    } else if (error.message.includes("Server error: 403")) {
      userMessage =
        "You don't have permission to access social media accounts data.";
    }

    return {
      success: false,
      data: { accounts: [], totalAccounts: 0 },
      message: userMessage,
      isEmpty: true,
      isError: true,
    };
  }
};

// New Code
// UPDATED: Enhanced postToSocialMedia function with parallel execution
export const postToSocialMedia = async (payload) => {
  try {
    console.log("Processing social media post with payload:", payload);

    // UPDATED: Create promises for all platforms simultaneously (no waiting/dependencies)
    const platformPromises = [];
    const platformsToProcess = [];

    // Process each platform separately and create promises for parallel execution
    for (const platform of payload.platforms) {
      const platformAccounts = payload.accounts.filter(
        (account) =>
          normalizeplatformName(account.provider) ===
          normalizeplatformName(platform),
      );

      if (platformAccounts.length === 0) {
        console.log(`No ${platform} accounts found, skipping...`);
        continue;
      }

      // Prepare platform-specific payload
      const platformPayload = {
        ...payload,
        accounts: platformAccounts,
        platforms: [platform], // Only this platform for this endpoint
      };

      platformsToProcess.push({
        platform: platform,
        accounts: platformAccounts,
      });

      // UPDATED: Create promise for each platform without awaiting - calling from socialMediaAPI.js
      let platformPromise;

      // Route to platform-specific endpoints based on content type and platform
      switch (normalizeplatformName(platform)) {
        case "instagram":
          console.log(
            `Creating Instagram ${payload.contentType} post promise...`,
          );
          platformPromise = postToInstagram(platformPayload)
            .then((result) => ({
              platform,
              accounts: platformAccounts,
              result,
              success: result.success,
            }))
            .catch((error) => ({
              platform,
              accounts: platformAccounts,
              error: error.message,
              success: false,
            }));
          break;

        case "linkedin":
          console.log(
            `Creating LinkedIn ${payload.contentType} post promise...`,
          );
          platformPromise = postToLinkedIn(platformPayload)
            .then((result) => ({
              platform,
              accounts: platformAccounts,
              result,
              success: result.success,
            }))
            .catch((error) => ({
              platform,
              accounts: platformAccounts,
              error: error.message,
              success: false,
            }));
          break;

        case "twitter": // UPDATED: Handles both "Twitter" and "Twitter (X)"
          console.log(
            `Creating Twitter ${payload.contentType} post promise...`,
          );
          platformPromise = postToTwitter(platformPayload)
            .then((result) => ({
              platform,
              accounts: platformAccounts,
              result,
              success: result.success,
            }))
            .catch((error) => ({
              platform,
              accounts: platformAccounts,
              error: error.message,
              success: false,
            }));
          break;

        case "facebook":
          console.log(
            `Creating Facebook ${payload.contentType} post promise...`,
          );
          platformPromise = postToFacebook(platformPayload)
            .then((result) => ({
              platform,
              accounts: platformAccounts,
              result,
              success: result.success,
            }))
            .catch((error) => ({
              platform,
              accounts: platformAccounts,
              error: error.message,
              success: false,
            }));
          break;

        case "youtube":
          console.log(
            `Creating YouTube ${payload.contentType} post promise...`,
          );
          platformPromise = postToYoutube(platformPayload)
            .then((result) => ({
              platform,
              accounts: platformAccounts,
              result,
              success: result.success,
            }))
            .catch((error) => ({
              platform,
              accounts: platformAccounts,
              error: error.message,
              success: false,
            }));
          break;

        default:
          console.log(
            `Creating ${platform} ${payload.contentType} post promise using general endpoint...`,
          );
          platformPromise = Promise.resolve({
            platform,
            accounts: platformAccounts,
            error: `${platform} posting not yet implemented`,
            success: false,
          });
          break;
      }

      // Add promise to array for parallel execution
      platformPromises.push(platformPromise);
    }

    // UPDATED: Execute all platform calls in parallel using Promise.allSettled
    console.log(
      `Starting ${platformPromises.length} platform API calls in parallel...`,
    );
    const startTime = Date.now();

    const platformResults = await Promise.allSettled(platformPromises);

    const endTime = Date.now();
    console.log(`All platform API calls completed in ${endTime - startTime}ms`);

    // Process results from all platforms
    const successfulPlatforms = [];
    const failedPlatforms = [];
    let totalSuccess = 0;
    let totalFailure = 0;

    platformResults.forEach((promiseResult, index) => {
      const platformInfo = platformsToProcess[index];

      if (promiseResult.status === "fulfilled") {
        const platformResult = promiseResult.value;

        if (platformResult.success) {
          successfulPlatforms.push(platformResult);
          totalSuccess += platformResult.accounts.length;
          console.log(
            `✅ ${platformResult.platform} posting completed successfully`,
          );
        } else {
          failedPlatforms.push(platformResult);
          totalFailure += platformResult.accounts.length;
          console.log(
            `❌ ${platformResult.platform} posting failed: ${
              platformResult.error || platformResult.result?.message
            }`,
          );
        }
      } else {
        // Promise was rejected
        failedPlatforms.push({
          platform: platformInfo.platform,
          accounts: platformInfo.accounts,
          error: promiseResult.reason?.message || "Unknown error occurred",
          success: false,
        });
        totalFailure += platformInfo.accounts.length;
        console.log(
          `❌ ${platformInfo.platform} posting failed: ${promiseResult.reason?.message}`,
        );
      }
    });

    // Create comprehensive summary
    const totalAccounts = payload.accounts.length;
    const totalPlatforms = payload.platforms.length;

    console.log(
      `📊 Final Results: ${totalSuccess}/${totalAccounts} accounts successful across ${successfulPlatforms.length}/${totalPlatforms} platforms`,
    );

    // Create summary message
    let summaryMessage;
    if (totalSuccess === totalAccounts && failedPlatforms.length === 0) {
      // Complete success
      const fileCount =
        payload.mediaFiles?.length || (payload.mediaFile ? 1 : 0);
      const contentInfo =
        payload.contentType === "Image"
          ? `with ${fileCount} file${fileCount > 1 ? "s" : ""}`
          : payload.contentType === "Video"
            ? `with ${fileCount} video${fileCount > 1 ? "s" : ""}`
            : "";

      summaryMessage = `🎉 ${
        payload.contentType
      } post ${contentInfo} successfully sent to all ${totalSuccess} account${
        totalSuccess > 1 ? "s" : ""
      } across all ${successfulPlatforms.length} platform${
        successfulPlatforms.length > 1 ? "s" : ""
      }!`;
    } else if (totalSuccess > 0) {
      // Partial success
      const successfulPlatformNames = successfulPlatforms
        .map((p) => p.platform)
        .join(", ");
      const failedPlatformNames = failedPlatforms
        .map((p) => p.platform)
        .join(", ");

      summaryMessage = `⚠️ Partial success: Posted to ${successfulPlatformNames} (${totalSuccess} accounts). Failed: ${failedPlatformNames} (${totalFailure} accounts).`;
    } else {
      // Complete failure
      summaryMessage = `💥 Failed to send ${payload.contentType.toLowerCase()} post to all platforms. Please check your account connections and try again.`;
    }

    return {
      success: totalSuccess > 0,
      data: {
        platformResults: successfulPlatforms,
        platformErrors: failedPlatforms,
        summary: {
          totalAccounts: totalAccounts,
          totalPlatforms: totalPlatforms,
          successfulAccounts: totalSuccess,
          failedAccounts: totalFailure,
          successfulPlatforms: successfulPlatforms.length,
          failedPlatforms: failedPlatforms.length,
          successRate: Math.round((totalSuccess / totalAccounts) * 100),
          platformSuccessRate: Math.round(
            (successfulPlatforms.length / totalPlatforms) * 100,
          ),
          executionTime: endTime - startTime,
        },
        detailedResults: platformResults,
      },
      message: summaryMessage,
      endpoint: "Multiple platform-specific endpoints (parallel execution)",
      isPartialSuccess: totalSuccess > 0 && totalFailure > 0,
      isPlatformSpecific: true,
      isParallelExecution: true,
    };
  } catch (error) {
    console.error("API Network Error in parallel execution:", error);

    let errorMessage = "Failed to connect to social media APIs";

    if (error.name === "TypeError" && error.message.includes("fetch")) {
      errorMessage =
        "Network error: Unable to connect to social media APIs. Please check your internet connection.";
    } else if (error.name === "AbortError") {
      errorMessage =
        "Request timeout during parallel execution. Please check your connection and try again.";
    } else {
      errorMessage = error.message || errorMessage;
    }

    return {
      success: false,
      message: errorMessage,
      endpoint:
        "Multiple platform-specific endpoints (parallel execution failed)",
      isParallelExecution: true,
    };
  }
};

// UPDATED: Convert platform name for switch case matching
const normalizeplatformName = (platform) => {
  switch (platform.toLowerCase()) {
    case "twitter (x)":
    case "twitter":
      return "twitter";
    default:
      return platform.toLowerCase();
  }
};

// Add this function with your existing API functions
export const fetchMonthlyPosts = async (monthYear, clientId) => {
  try {
    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/manage-post/runPost?clientId=${clientId}&DataMonth=${monthYear}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      },
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: [],
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status && result.data) {
      return {
        success: true,
        data: result.data.posts || [], // Extract posts array from nested data
        totalPosts: result.data.totalPosts,
        dateRange: result.data.dateRange,
        message: result.message,
      };
    }

    return {
      success: false,
      data: [],
      message: result.message || "Failed to fetch monthly posts",
    };
  } catch (error) {
    console.error("Error in fetchMonthlyPosts:", error);
    return {
      success: false,
      data: [],
      message: error.message,
    };
  }
};

// Add Secure Account API
export const addSecureAccount = async (accountData) => {
  console.log("addSecureAccount called with data:", accountData);

  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: null,
        message: "Client ID not found. Please log in again.",
      };
    }

    const token = getAuthToken();
    const response = await fetch(`${API_URL}/secure-accounts/${clientId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `${token}` } : {}),
      },
      body: JSON.stringify(accountData),
    });

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: null,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (result.status) {
      return {
        success: true,
        data: result.data,
        message: result.message || "Secure account added successfully",
      };
    } else {
      return {
        success: false,
        data: null,
        message: result.message || "Failed to add secure account",
      };
    }
  } catch (error) {
    console.error("Error in addSecureAccount:", error);
    return {
      success: false,
      data: null,
      message:
        error.message || "Network error occurred while adding secure account",
    };
  }
};

// Get Secure Accounts API
export const getSecureAccounts = async () => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: [],
        message: "Client ID not found. Please log in again.",
      };
    }
    const token = getAuthToken();
    const response = await fetch(`${API_URL}/secure-accounts/${clientId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `${token}` } : {}),
      },
    });

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: [],
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    if (result.status && Array.isArray(result.data)) {
      return {
        success: true,
        data: result.data,
        message: result.message || "Secure accounts loaded successfully",
      };
    } else {
      return {
        success: false,
        data: [],
        message: result.message || "Failed to load secure accounts",
      };
    }
  } catch (error) {
    console.error("Error in getSecureAccounts:", error);
    return {
      success: false,
      data: [],
      message:
        error.message || "Network error occurred while loading secure accounts",
    };
  }
};

/**
 * Patch (edit) a secure account
 * @param {string} secureAccountId - The ID of the secure account to edit
 * @param {Object} data - The fields to update
 * @returns {Promise<Object>} API response
 */
export const patchSecureAccount = async (secureAccountId, data) => {
  try {
    const clientId = getClientId();
    if (!clientId) {
      return {
        success: false,
        data: null,
        message: "Client ID not found. Please log in again.",
      };
    }
    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/secure-accounts/${clientId}/${secureAccountId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
        body: JSON.stringify(data),
      },
    );
    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: null,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }
    if (
      response.ok &&
      (result.status === undefined || result.status === true)
    ) {
      return {
        success: true,
        data: result.data || null,
        message: result.message || "Secure account updated successfully",
      };
    } else {
      return {
        success: false,
        data: null,
        message: result.message || "Failed to update secure account",
      };
    }
  } catch (error) {
    console.error("Error in patchSecureAccount:", error);
    return {
      success: false,
      data: null,
      message:
        error.message || "Network error occurred while updating secure account",
    };
  }
};

/**
 * Fetch the monthly post count for a client
 * @param {string} clientId - The client ID (r1)
 * @param {string} monthYear - The month-year string e.g. "Mar-2026" (r2)
 * @returns {Promise<Object>} API response with postCount
 */
export const fetchMonthlyPostCount = async (clientId, monthYear) => {
  try {
    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/util-class/${clientId}/${monthYear}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
      }
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        data: null,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // API returns data even when postCount is 0
    return {
      success: true,
      data: result.data || { clientId, monthYear, postCount: 0 },
      message: result.uiMsg || result.message || "Post count loaded",
    };
  } catch (error) {
    console.error("Error in fetchMonthlyPostCount:", error);
    return {
      success: false,
      data: null,
      message:
        error.message || "Network error occurred while fetching post count",
    };
  }
};

/**
 * Increment the monthly post count for a client (called after a successful post)
 * @param {string} clientId - The client ID
 * @param {string} monthYear - The month-year string e.g. "Mar-2026"
 * @returns {Promise<Object>} API response
 */
export const incrementMonthlyPostCount = async (clientId, monthYear, body = {}) => {
  try {
    const token = getAuthToken();
    const response = await fetch(
      `${API_URL}/util-class/${clientId}/${monthYear}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        },
        body: JSON.stringify(body),
      }
    );

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return {
      success: true,
      data: result.data || null,
      message: result.uiMsg || result.message || "Post count updated",
    };
  } catch (error) {
    console.error("Error in incrementMonthlyPostCount:", error);
    return {
      success: false,
      data: null,
      message:
        error.message || "Network error occurred while updating post count",
    };
  }
};
/**
 * Close a client account permanently
 * @param {string} clientId - The client ID
 * @param {string[]} userIds - Array of user IDs to close
 * @returns {Promise<Object>} API response
 */
export const closeClientAccount = async (clientId, userIds) => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${API_URL}/util-class/closeAccount`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `${token}` } : {}),
      },
      body: JSON.stringify({ clientId, userIds }),
    });

    const result = await response.json().catch(() => ({}));

    if (handleSessionTimeout(response.status, result)) {
      return {
        success: false,
        message: result.uiMsg || result.message || "Session timeout",
      };
    }

    if (!response.ok) {
      return {
        success: false,
        message: result.uiMsg || result.message || `Request failed (${response.status})`,
      };
    }

    return {
      success: true,
      message: result.uiMsg || result.message || "Account closed successfully",
    };
  } catch (error) {
    console.error("Error in closeClientAccount:", error);
    return {
      success: false,
      message: error.message || "Network error occurred while closing account",
    };
  }
};

/**
 * Fetch Facebook Analytics Data for a specific page
 * @param {string} pageId - The Facebook page ID
 * @param {string} accessToken - The Facebook access token
 * @returns {Promise<Object>} API response with Facebook analytics data
 */
export const fetchFacebookAnalytics = async (pageId, accessToken) => {
  console.log('fetchFacebookAnalytics called with:', { pageId, accessToken: accessToken ? '***' : 'missing' });
  
  try {
    const requestBody = {
      pageId: pageId,
      accessToken: accessToken,
    };
    
    console.log('Making POST request to https://n8n.insyts.co/webhook/facebook');
    
    const response = await fetch('https://n8n.insyts.co/webhook/facebook', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('Response status:', response.status);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Response data:', result);

    // Check if result is an array or contains data array
    const analyticsData = Array.isArray(result) ? result : (result.data || []);

    return {
      success: true,
      data: analyticsData,
      message: "Facebook analytics loaded successfully",
    };
  } catch (error) {
    console.error("Error in fetchFacebookAnalytics:", error);
    return {
      success: false,
      data: [],
      message: error.message || "Network error occurred while fetching Facebook analytics",
    };
  }
};

// /**
//  * Fetch Instagram analytics data
//  * @param {string} pageId - The Instagram Business Account ID
//  * @param {string} accessToken - The Instagram access token
//  * @returns {Promise<Object>} API response with Instagram analytics data
//  */
// export const fetchInstagramAnalytics = async (pageId, accessToken) => {
//   console.log('fetchInstagramAnalytics called with:', { pageId, accessToken: accessToken ? '***' : 'missing' });
  
//   try {
//     const requestBody = {
//       pageId: pageId,
//       accessToken: accessToken,
//     };
    
//     console.log('Making POST request to https://n8n.insyts.co/webhook/instagram-dashboard-view');
    
//     const response = await fetch('https://n8n.insyts.co/webhook/instagram-dashboard-view', {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify(requestBody),
//     });

//     console.log('Response status:', response.status);

//     if (!response.ok) {
//       throw new Error(`HTTP error! status: ${response.status}`);
//     }

//     const result = await response.json();
//     console.log('Response data:', result);

//     // Result should be an array containing profile and media data
//     const analyticsData = Array.isArray(result) ? result : (result.data || []);

//     return {
//       success: true,
//       data: analyticsData,
//       message: "Instagram analytics loaded successfully",
//     };
//   } catch (error) {
//     console.error("Error in fetchInstagramAnalytics:", error);
//     return {
//       success: false,
//       data: [],
//       message: error.message || "Network error occurred while fetching Instagram analytics",
//     };
//   }
// };